import { Fragment } from "react"

import {
  Card,
  CardContent,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  Skeleton,
  useMediaQuery,
} from "@mui/material"
import theme from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import { useParsedHtml, useRemainingCapacity } from "@/hooks"
import useProductTotals from "@/hooks/useProductTotals"
import { MemberInformation } from "@/queries/models/Product"
import { useSkipassStore } from "@/store/skipassStore"
import { buildSelectedProduct } from "@/utils/productMappers"

import { AdditionalDescription, PriceDisplay } from "./components"
import { DealBadge as DealBadgeComponent } from "./components/DealBadge"
import ProductCardMedia from "./components/ProductCardMedia"
import type { AgeGroupWithPrice, DealBadge } from "./ProductCard.types"
import Counter from "../Counter/Counter"

const DEFAULT_MAX_QUANTITY = 10

type ProductCardProps = {
  productId: string
  productName: string
  imageId: number
  description: string
  additionalDescription?: string
  actionNote?: string
  memberInformation?: MemberInformation
  ageGroups: AgeGroupWithPrice[]
  disabledMessage?: string
  onClick?: () => void
  idImageRequired: boolean
  isSubscriptionProduct: boolean
  isLoading?: boolean
  personCertificateRequired: boolean
  bookingLengthInDays: number
  bookingLengthName?: string
  dealBadge?: DealBadge
  showFreeSkiDays: boolean
  subscriptionConfigurationId: string | null
  resortId: number
  capacityGroupId?: string
  category: "SkiPass" | "Ticket"
  onDetailComplete?: (detail: {
    id: string
    name: string
    price: number
    currencyCode: string
    category: "SkiPass" | "Ticket"
  }) => void
}

const ProductCard = ({
  productId,
  productName,
  description,
  additionalDescription,
  memberInformation,
  ageGroups,
  imageId,
  actionNote,
  disabledMessage,
  idImageRequired,
  isSubscriptionProduct,
  isLoading = false,
  personCertificateRequired,
  bookingLengthInDays,
  bookingLengthName,
  dealBadge,
  showFreeSkiDays,
  subscriptionConfigurationId,
  resortId,
  capacityGroupId,
  category,
  onDetailComplete,
}: ProductCardProps) => {
  const { t } = useTranslation()
  const { getProductQuantity } = useProductTotals()
  const incrementProduct = useSkipassStore((state) => state.incrementProduct)
  const decrementProduct = useSkipassStore((state) => state.decrementProduct)

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const isDisabled = !!disabledMessage

  const { remainingCapacity } = useRemainingCapacity({
    capacityGroupId,
    ageGroups,
  })

  const parsedAndSanatizedDescription = useParsedHtml(description)

  const productInfo = {
    productId,
    productName,
    idImageRequired,
    isSubscriptionProduct,
    personCertificateRequired,
    bookingLengthInDays,
    showFreeSkiDays,
    subscriptionConfigurationId,
    resortId,
  }

  const ageGroupsWithPrices = ageGroups.filter(
    (ageGroup) => ageGroup.id !== "-1"
  )
  const freeAgeGroup = ageGroups.find((ageGroup) => ageGroup.id === "-1")

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 450,
        height: "100%",
        position: "relative",
      }}
      data-testid="product-card"
    >
      {isDisabled && <CardOverlay />}

      <ProductCardMedia
        imageId={imageId}
        productName={productName}
        actionNote={actionNote}
        memberInformation={memberInformation}
        statusText={disabledMessage}
        topBadge={dealBadge && <DealBadgeComponent {...dealBadge} />}
      />
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          "&:last-child": {
            pb: 1,
          },
        }}
      >
        <Typography
          component="h2"
          fontWeight="bold"
          sx={{
            fontSize: isMobile ? "body1" : "20px",
          }}
        >
          {productName}
        </Typography>
        <Typography variant={isMobile ? "body3" : "body2"} color="secondary">
          {parsedAndSanatizedDescription}
        </Typography>
        {additionalDescription && (
          <AdditionalDescription
            productName={productName}
            description={description}
            additionalDescription={additionalDescription}
          />
        )}
        <List disablePadding>
          {ageGroupsWithPrices.map((ageGroup) => {
            const {
              id,
              name,
              ageSpan,
              price,
              productId: productPriceId,
            } = ageGroup
            return (
              <Fragment key={id}>
                <ListItem
                  disableGutters
                  data-testid={`age-group-${name}`}
                  sx={{
                    py: 2,
                    minHeight: { md: 104 },
                    alignItems: { md: "center" },
                  }}
                >
                  <LoadingWrapper isLoading={isLoading}>
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: { md: 1 },
                      }}
                    >
                      <ListItemText
                        sx={{ mb: 0 }}
                        primary={`${name} (${ageSpan})`}
                        slotProps={{
                          primary: {
                            variant: isMobile ? "body3" : "body2",
                            sx: {
                              fontWeight: 600,
                            },
                          },
                        }}
                      />
                      <PriceDisplay
                        price={price}
                        isSubscriptionPrice={isSubscriptionProduct}
                      />
                    </Box>

                    {price && (
                      <Box ml={1}>
                        <Counter
                          value={getProductQuantity(productPriceId)}
                          onIncrement={() => {
                            incrementProduct(
                              buildSelectedProduct(productInfo, ageGroup)
                            )
                            onDetailComplete?.({
                              id: ageGroup.analyticsId,
                              name: ageGroup.analyticsName,
                              price: price.discountedPrice
                                ? price.discountedPrice.price
                                : price.onlinePrice,
                              currencyCode: price.suffix,
                              category,
                            })
                          }}
                          onDecrement={() => decrementProduct(productPriceId)}
                          disabled={isDisabled}
                          maxValue={
                            remainingCapacity !== undefined
                              ? getProductQuantity(productPriceId) +
                                remainingCapacity
                              : DEFAULT_MAX_QUANTITY
                          }
                          label={`${name} (${ageSpan})`}
                        />
                      </Box>
                    )}
                  </LoadingWrapper>
                </ListItem>
                <Divider />
              </Fragment>
            )
          })}
          {freeAgeGroup && (
            <>
              <ListItem disableGutters>
                <LoadingWrapper isLoading={isLoading}>
                  <ListItemText
                    primary={`${freeAgeGroup.name}`}
                    secondary={t("productCard.freeLabel")}
                    slotProps={{
                      primary: {
                        variant: isMobile ? "body3" : "body2",
                        fontWeight: "600",
                      },
                      secondary: {
                        variant: isMobile ? "body3" : "body2",
                        fontWeight: "600",
                        color: "text.primary",
                      },
                    }}
                  />
                </LoadingWrapper>
              </ListItem>
              <Divider />
            </>
          )}
        </List>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textAlign: "right",
            mt: "auto",
          }}
        >
          {bookingLengthName}
        </Typography>
      </CardContent>
    </Card>
  )
}

const LoadingWrapper = ({
  isLoading,
  children,
}: {
  isLoading: boolean
  children: React.ReactNode
}) => {
  return isLoading ? (
    <Skeleton
      width={"100%"}
      height={55}
      aria-label="Loading product price information"
      role="status"
      aria-live="polite"
    />
  ) : (
    <>{children}</>
  )
}

const CardOverlay = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "#D9D9D980",
        borderRadius: 1,
        zIndex: 10,
        display: "block",
      }}
    />
  )
}

export default ProductCard
